// ---------------- External imports ------------------ //
import { useState } from 'react'
import PropTypes from 'prop-types'
import { withLocalize } from 'react-localize-redux'

// ---------------- Material-UI imports ------------------ //
import { Button, Modal } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import SyncIcon from '@mui/icons-material/Sync'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

// ---------------- Local imports ------------------ //
import styles from './DeleteDeviceContainer.module.scss'
import { chatarrizarDevices, checkChatarrizarDevices } from 'api/system'
import { MigrationProgress } from '../../MigrationProgress/MigrationProgress'

const DeleteDeviceContainer = props => {
  const [open] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [devicesStatus, setDeviceStatus] = useState(null)

  const { hideModal, translate, devices, deleteSystems } = props

  const handleSubmit = async () => {
    const handleBeforeUnload = event => {
      event.preventDefault()
      event.returnValue = '' // This is required for Chrome to show the warning dialog
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    setIsSubmitting(true)
    const response = await chatarrizarDevices({
      deviceIds: devices.map(device => device._id)
    })

    if (response.message === 'ok') {
      const deviceStatus = devices.map(device => ({
        _id: device._id,
        status: 'processing'
      }))
      setDeviceStatus(deviceStatus)

      let checkResponse = await checkChatarrizarDevices({
        deviceIds: devices.map(device => device._id)
      })
      setDeviceStatus(checkResponse.devicesStatus)
      let anyProcessing = checkResponse.devicesStatus.some(device => device.status === 'processing')

      while (anyProcessing) {
        // Check every 5 seconds
        await new Promise(resolve => setTimeout(resolve, 5000))
        checkResponse = await checkChatarrizarDevices({
          deviceIds: devices.map(device => device._id)
        })
        setDeviceStatus(checkResponse.devicesStatus)
        anyProcessing = checkResponse.devicesStatus.some(device => device.status === 'processing')
      }
      // Check which devices were successfully deleted (status === 'done')
      const deviceIdsToDelete = checkResponse.devicesStatus
        .filter(device => device.status === 'done')
        .map(device => device._id)
      if (deviceIdsToDelete.length > 0) {
        // Dispatch action to delete systems
        deleteSystems({ ids: deviceIdsToDelete })
      }
    }
  }

  const handleClose = () => {
    hideModal()
  }

  let isProcessing =
    devicesStatus === null ? true : devicesStatus?.some(device => device.status === 'processing')

  return (
    <div>
      <Modal
        open={open}
        onClose={hideModal}
        arial-labelledby='simple-modal-title'
        arial-desccribedby='simple-modal-description'
        BackdropProps={{
          classes: {
            root: styles.backdrop
          }
        }}
      >
        <div className={styles.modalContainer}>
          <div className={styles.header}>
            <h2>{translate('deleteDevice')}</h2>
          </div>
          {!devicesStatus && (
            <div className={styles.content}>
              <p>{translate('deleteDeviceInfo')}</p>
              <br />
              {devices.length === 1 && (
                <p>
                  {translate('confirmDeleteDevice') + ': '} <br />{' '}
                  <span className={styles.deviceID}>{devices[0].MacAddressId}</span>
                </p>
              )}
              {devices.length > 1 && (
                <p className={styles.multipleDevices}>
                  {translate('confirmDeleteDevices') + ': '} <br />
                  {devices.map(device => (
                    <span
                      key={device._id}
                      className={styles.deviceID}
                    >
                      {device.MacAddressId}
                    </span>
                  ))}
                </p>
              )}
            </div>
          )}

          {devicesStatus?.length > 0 && (
            <div className={styles.statusContainer}>
              {devices.map(device => {
                const deviceState = devicesStatus.find(d => d._id === device._id)
                const status = deviceState?.status
                const isError = status === 'delete_error' || status === 'stale'
                return (
                  <div
                    key={device._id}
                    className={styles.statusItem}
                  >
                    <div className={styles.statusRow}>
                      <span className={styles.deviceID}>{device.MacAddressId}</span>
                      <span className={styles.status}>
                        {status === 'done' ? (
                          <CheckCircleOutlineIcon style={{ color: 'green' }} />
                        ) : isError ? (
                          <ErrorOutlineIcon style={{ color: 'red' }} />
                        ) : (
                          <SyncIcon className={styles.spinner} />
                        )}
                      </span>
                    </div>
                    {status === 'processing' && (
                      <MigrationProgress progress={deviceState?.progress} />
                    )}
                    {status === 'stale' && (
                      <small style={{ color: 'red' }}>
                        El borrado parece haberse detenido (posible timeout). Puede relanzarse.
                      </small>
                    )}
                    {status === 'delete_error' && (
                      <small style={{ color: 'red' }}>
                        Error en el borrado. El dispositivo no se ha eliminado; puede reintentarse.
                      </small>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {isSubmitting && !isProcessing && (
            <div className={styles.buttonsWrapper}>
              <Button
                onClick={handleClose}
                className={styles.buttonCancelar}
              >
                {translate('cancel')}
              </Button>
            </div>
          )}

          {isProcessing && (
            <div className={styles.buttonsWrapper}>
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                className={styles.buttonCancelar}
              >
                {translate('cancel')}
              </Button>
              <LoadingButton
                disableRipple
                className={styles.submitButton}
                loading={isSubmitting}
                variant='contained'
                loadingPosition='end'
                disabled={isSubmitting}
                isLoading={isSubmitting}
                onClick={handleSubmit}
              >
                {translate('delete')}
              </LoadingButton>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

DeleteDeviceContainer.propTypes = {
  hideModal: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired
}

export default withLocalize(DeleteDeviceContainer)
